import posthog from 'posthog-js'
import React, {useState} from 'react'
import {Block, Col} from 'jsxstyle'
import Tooltip from '../../../../components/Tooltip/Tooltip'
import i18n from '../../../../common/i18n'
import {useXpertKnowledgeAuthContext} from '../../../../components/AuthContext/XpertKnowledgeAuthContext'
import {ReactComponent as EditIcon} from '../../../../common/assets/edit-icon.svg'
import {ReactComponent as RegenerateIcon} from '../../../../common/assets/regenerate-icon.svg'
import {ReactComponent as DevIcon} from '../../../../common/assets/dev-icon.svg'
import {ReactComponent as UnpublishIcon} from '../../../../common/assets/shield-x-icon.svg'
import {ReactComponent as SaveIcon} from '../../../../common/assets/save-icon.svg'
import {ReactComponent as GlobeIcon} from '../../../../common/assets/globe-icon.svg'
import {ReactComponent as XCircleIcon} from '../../../../common/assets/x-circle-icon.svg'
import {useXpertKnowledgeContext} from '../../../XpertKnowledge/XpertKnowledgeContext'
import {useNavigate} from 'react-router-dom'
import IconTextButton from '../../../../components/IconTextButton'
import {UserRole} from '../../../../services/User/type'
import {useXpertKnowledgeTutorialContext} from '../../XpertKnowledgeTutorialContext'
import {XpertKnowledgeTutorialGenerationType, XpertKnowledgeTutorialStatus} from '../../../../services/XpertKnowledgeTutorial/type'
import QuickConfirmDialog from '../../../../components/QuickConfirmDialog'

const XpertKnowledgeActions: React.FC = () => {
  const {selectedXpertKnowledge, regenerateXpertKnowledge, makeXpertKnowledgeTutorialPublic, makeXpertKnowledgeTutorialPrivate} =
    useXpertKnowledgeContext()
  const {selectedXpertKnowledgeTutorial, downgradeXpertKnowledgeTutorial, dismissReviewXpertKnowledgeTutorial} = useXpertKnowledgeTutorialContext()
  const [openRegenerateConfirm, setOpenRegenerateConfirm] = useState(false)
  const [openUnpublishConfirm, setOpenUnpublishConfirm] = useState(false)
  const [openBackToDraftConfirm, setOpenBackToDraftConfirm] = useState(false)
  const {user} = useXpertKnowledgeAuthContext()
  const navigate = useNavigate()

  const [hoveredTooltip, setHoveredTooltip] = useState<string | null>(null)

  const handleTooltipHover = (tooltipName: string) => {
    setHoveredTooltip(tooltipName)
  }

  const handleTooltipLeave = () => {
    setHoveredTooltip(null)
  }

  const handlePublicClick = () => {
    if (!selectedXpertKnowledge?.id) return
    if (selectedXpertKnowledge.isPublic) {
      makeXpertKnowledgeTutorialPrivate(selectedXpertKnowledge.id)
      posthog.capture('tutorial_public_link_deleted')
    } else {
      makeXpertKnowledgeTutorialPublic(selectedXpertKnowledge.id)
      posthog.capture('tutorial_public_link_created')
    }
  }

  return (
    <>
      <Col padding="24px" borderRadius="12px" gap="12px" boxSizing="border-box" backgroundColor="#FFFFFF" border="1px solid #E0E0E0">
        <Col gap="8px">
          <Block fontWeight={400} fontSize="12px" lineHeight="21px" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" color="#000000">
            {i18n.t('xk.tutorial.adminDropdown.actions')}
          </Block>
          {((user?.role !== UserRole.AGENT &&
            selectedXpertKnowledgeTutorial?.status !== XpertKnowledgeTutorialStatus.REFUSED &&
            selectedXpertKnowledgeTutorial?.status !== XpertKnowledgeTutorialStatus.DRAFT) ||
            (user?.role === UserRole.AGENT &&
              selectedXpertKnowledgeTutorial?.status !== XpertKnowledgeTutorialStatus.PUBLISHED &&
              selectedXpertKnowledgeTutorial?.status !== XpertKnowledgeTutorialStatus.IN_REVIEW)) && (
            <IconTextButton
              text={i18n.t('xk.tutorial.adminDropdown.edit')}
              icon={<EditIcon />}
              onClick={() => {
                posthog.capture('tutorial_edit_clicked')
                navigate('edit')
              }}
              color="#7A42C2"
            />
          )}
          {user?.role === UserRole.ADMIN_SIGHTCALL && (
            <IconTextButton
              text={i18n.t('xk.tutorial.adminDropdown.debug')}
              icon={<SaveIcon />}
              onClick={() => navigate(`/xpert-knowledge/${selectedXpertKnowledge?.id}/tutorial/debug`)}
              color="#7A42C2"
            />
          )}
          {user?.role === UserRole.ADMIN_SIGHTCALL && (
            <IconTextButton
              text={i18n.t('xk.tutorial.adminDropdown.regenerate')}
              icon={<RegenerateIcon />}
              onClick={() => setOpenRegenerateConfirm(true)}
              color="#7A42C2"
            />
          )}
          {user?.role !== UserRole.AGENT && (
            <IconTextButton
              text={i18n.t('xk.tutorial.adminDropdown.assets')}
              icon={<SaveIcon />}
              onClick={() => navigate(`/xpert-knowledge/${selectedXpertKnowledge?.id}/assets`)}
              color="#7A42C2"
            />
          )}
          {user?.role === UserRole.AGENT && selectedXpertKnowledgeTutorial?.status == XpertKnowledgeTutorialStatus.IN_REVIEW && (
            <IconTextButton
              text={i18n.t('xk.tutorial.adminDropdown.dismissReview')}
              icon={<UnpublishIcon />}
              onClick={() => setOpenBackToDraftConfirm(true)}
              color="#7A42C2"
            />
          )}
          {user?.role === UserRole.ADMIN_SIGHTCALL && selectedXpertKnowledgeTutorial?.status == XpertKnowledgeTutorialStatus.PUBLISHED && (
            <IconTextButton
              text={i18n.t('xk.tutorial.adminDropdown.downgrade')}
              icon={<UnpublishIcon />}
              onClick={() => setOpenUnpublishConfirm(true)}
              color="#7A42C2"
            />
          )}
          {user?.role !== UserRole.AGENT && selectedXpertKnowledgeTutorial?.status == XpertKnowledgeTutorialStatus.PUBLISHED && (
            <Block
              display="flex"
              position="relative"
              props={{
                onMouseEnter: () => handleTooltipHover('public'),
                onMouseLeave: handleTooltipLeave,
              }}
            >
              <IconTextButton
                text={
                  selectedXpertKnowledge?.isPublic ? i18n.t('xk.tutorial.adminDropdown.makePrivate') : i18n.t('xk.tutorial.adminDropdown.makePublic')
                }
                icon={selectedXpertKnowledge?.isPublic ? <XCircleIcon /> : <GlobeIcon />}
                onClick={() => handlePublicClick()}
                color="#7A42C2"
              />

              <Tooltip name="public" hoveredTooltip={hoveredTooltip}>
                {selectedXpertKnowledge?.isPublic
                  ? i18n.t('xk.tutorial.adminDropdown.makePrivateTooltip')
                  : i18n.t('xk.tutorial.adminDropdown.makePublicTooltip')}
              </Tooltip>
            </Block>
          )}
          {user?.role === UserRole.ADMIN_SIGHTCALL && (
            <IconTextButton
              text={i18n.t('xk.tutorial.adminDropdown.prompt')}
              icon={<DevIcon />}
              onClick={() => navigate(`/xpert-knowledge/${selectedXpertKnowledge?.id}/debug`)}
              color="#7A42C2"
            />
          )}
        </Col>
      </Col>
      <QuickConfirmDialog
        key={selectedXpertKnowledgeTutorial?.id}
        open={openRegenerateConfirm}
        maxWidth="580px"
        setOpen={() => setOpenRegenerateConfirm(false)}
        title={i18n.t('xk.tutorial.regenerateDialogs.regenerateTitle')}
        content={<Block whiteSpace="normal">{i18n.t('xk.tutorial.regenerateDialogs.regenerateContent')}</Block>}
        buttons={[
          {
            title: i18n.t('xk.tutorial.regenerateDialogs.buttonRegenerate'),
            onClick: () => {
              if (selectedXpertKnowledge) regenerateXpertKnowledge(selectedXpertKnowledge, XpertKnowledgeTutorialGenerationType.CLASSIC)
              navigate('/xpert-knowledge')
            },
            closeOnClick: true,
            customButtonProps: {
              backgroundColor: '#28A745',
              hoverBackgroundColor: '#218838',
              color: '#FFFFFF',
            },
          },
          {
            title: i18n.t('xk.tutorial.regenerateDialogs.buttonRegenerateWithDocs'),
            onClick: () => {
              if (selectedXpertKnowledge) regenerateXpertKnowledge(selectedXpertKnowledge, XpertKnowledgeTutorialGenerationType.WITH_DOCS)
              navigate('/xpert-knowledge')
            },
            closeOnClick: true,
            customButtonProps: {
              backgroundColor: '#e6420b',
              hoverBackgroundColor: '#c5300a',
              color: '#FFFFFF',
            },
          },
        ]}
      />
      <QuickConfirmDialog
        key={`unpublish-${selectedXpertKnowledgeTutorial?.id}`}
        open={openUnpublishConfirm}
        setOpen={() => setOpenUnpublishConfirm(false)}
        title={i18n.t('xk.tutorial.unpublishDialogs.unpublishTitle')}
        content={i18n.t('xk.tutorial.unpublishDialogs.unpublishContent')}
        buttons={[
          {
            title: i18n.t('xk.tutorial.unpublishDialogs.buttonUnpublish'),
            onClick: () => {
              downgradeXpertKnowledgeTutorial()
            },
            closeOnClick: true,
            customButtonProps: {
              backgroundColor: '#DC3545',
              hoverBackgroundColor: '#C82333',
              color: '#FFFFFF',
            },
          },
        ]}
      />
      <QuickConfirmDialog
        key={`backToDraft-${selectedXpertKnowledgeTutorial?.id}`}
        open={openBackToDraftConfirm}
        setOpen={() => setOpenBackToDraftConfirm(false)}
        title={i18n.t('xk.tutorial.dissmissReviewDialogs.dismissTitle')}
        content={i18n.t('xk.tutorial.dissmissReviewDialogs.dismissContent')}
        buttons={[
          {
            title: i18n.t('xk.tutorial.dissmissReviewDialogs.buttonDismiss'),
            onClick: () => {
              dismissReviewXpertKnowledgeTutorial()
            },
            closeOnClick: true,
            customButtonProps: {
              backgroundColor: '#DC3545',
              hoverBackgroundColor: '#C82333',
              color: '#FFFFFF',
            },
          },
        ]}
      />
    </>
  )
}

export default XpertKnowledgeActions
